import React, { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { intl } from "LocaleWrapper";
import { useStoreState, Session } from "ezStore";
import { useApolloClient } from "@apollo/client";

import { Box, Tab, Tabs } from "@mui/material";
import { Card, FilterValues, FilterValuesProps, Table } from "@cofano/cck";

import CargoFilter, { CargoFilterType, convertFilterToJSON } from "components/cargo/CargoFilter";
import StatusChanger from "view/statuses/components/changer/StatusChanger";

import { TableId, useBackendTable } from "components/table";
import RefreshButton from "components/refresh/RefreshButton";
import { countries } from "components/selects/CountrySelect";
import CommentIndicatorButton from "components/comments/CommentIndicatorButton";
import useTabs from "components/hooks/useTabs";

import CargosDownload from "view/cargos/components/download/CargosDownload";

import { DataFormat, Fields, hasOptionalFieldPermission, hasPluginPermission, Language, usePermission } from "imports/Utils";

import { DossierLink, LocationLink, ProductLink, RelationLink, ShipLink, VoyageLink } from "utils/LinkUtils";
import { FormattedDate, FormattedDateWindow } from "utils/DateUtils";

import CargoRelationChange from "./CargoRelationChange";

import { Cargos_BulkplanningtableQuery, CommentEntityType, StatusType } from "__generated__/graphql";
import { gql } from "__generated__/gql";
import { ChildPropsFromPagination } from "types/commons";
import { CharterSubtype, SortDirection } from "__generated__/enums";
import { RelationSelectOption } from "view/relations/components/selects/RelationSelect";
import { SalesCharterStatusSelectOption } from "view/charters/components/selects/SalesCharterStatusSelect";
import { StatusSelectOption } from "view/statuses/components/selects/StatusSelect";
import { CargoActionDate } from "view/cargos/components/date/CargoActionDate";

const TabItems = {
	planned: "planned",
	unplanned: "unplanned",
};

export type CargoFromQuery = ChildPropsFromPagination<Cargos_BulkplanningtableQuery, "cargos">;

const BulkPlanningTable = ({
	tableKey,
	setSelectedRows,
	setDragging,
	scrollTo,
	getCargosForComments,
	openComment,
}: {
	tableKey: number;
	setSelectedRows: (cargos: CargoFromQuery[]) => void;
	setDragging: (isDragging: boolean) => void;
	scrollTo: (callId: number) => void;
	getCargosForComments: (cargos: CargoFromQuery[]) => void;
	openComment: (id: number, entityType: CommentEntityType) => void;
}) => {

	const { selectedTab, selectTab } = useTabs({
		defaultSelected: TabItems.unplanned,
	});

	return (
		<React.Fragment>
			<Box sx={{ borderBottom: 1, borderColor: "divider", marginBottom: 2 }}>
				<Tabs
					value={selectedTab}
					onChange={(e, value) => selectTab(value)}
					indicatorColor="primary">
					<Tab value={TabItems.planned} label={Language("PLANNED_CARGOS")} />
					<Tab value={TabItems.unplanned} label={Language("UNPLANNED_CARGOS")} />
				</Tabs>
			</Box>

			{selectedTab === TabItems.planned && (
				<PlannedTable
					tableKey={tableKey}
					scrollTo={scrollTo}
					getCargosForComments={getCargosForComments}
					openComment={openComment} />
			)}

			{selectedTab === TabItems.unplanned && (
				<UnplannedTable
					tableKey={tableKey}
					setSelectedRows={setSelectedRows}
					setDragging={setDragging}
					getCargosForComments={getCargosForComments}
					openComment={openComment} />
			)}
		</React.Fragment>
	);
};

const UnplannedTable = ({ tableKey, setSelectedRows, setDragging, getCargosForComments, openComment }: {
	tableKey: number;
	setSelectedRows: (cargos: CargoFromQuery[]) => void;
	setDragging: (isDragging: boolean) => void;
	getCargosForComments: (cargos: CargoFromQuery[]) => void;
	openComment: (id: number, entityType: CommentEntityType) => void;
}) => {

	const client = useApolloClient();
	const session = useStoreState(state => state.session);
	const navigate = useNavigate();
	const { isAdmin, isCompanyUser, isMainUserLimited, isReadOnly } = usePermission();

	const { tableProps, filterProps, filterValuesProps, refetch, clearSelection } = useBackendTable({
		id: TableId.BulkPlanning,
		defaultSize: "COMPACT",
		defaultPageSize: 25,
		selectSettings: {
			isFullSelectEnabled: false,
			disabled: (row) => {
				const cargo = row as CargoFromQuery;
				const hasWindow = (cargo.actions.length > 0 && !cargo.actions.find(a => !a.start && !a.end));
				return !hasWindow;
			},
		},
		onChange: async(pagination, sort, filter, callback) => {
			try {
				const cargoSearch = convertFilterToJSON(filter) as Record<string, unknown>;
				delete cargoSearch.charterSubtype;
				delete cargoSearch.charterStatus;
				delete cargoSearch.brokers;

				const result = await client.query({
					query: CARGOS_QUERY,
					variables: {
						pagination: {
							...pagination,
							sortList: sort ?? [ {
								field: "id",
								direction: SortDirection.Desc,
							} ],
						},
						search: {
							...cargoSearch,
							id: filter.id ? (Number.isInteger(filter.id) ? filter.id : 0) : undefined,
							charterSearch: {
								charterSubtypes: filter.charterSubtype,
								statuses: filter.charterStatus?.map(({ name }: SalesCharterStatusSelectOption) => name) ?? [],
								brokers: filter.brokers?.map((option: RelationSelectOption) => option.id),
							},
							isUnplanned: true,
							plannableOnShip: true,
						},
					},
				});

				callback({
					content: result.data.cargos?.content ?? [],
					totalResults: result.data.cargos?.totalElements || 0,
				});
			} catch {
				callback({
					content: [],
					totalResults: 0,
				});
			}
		},
	});

	const reload = () => {
		setTimeout(() => refetch(), 250);
	};

	const onRowDoubleClick = useCallback((row: CargoFromQuery, event: MouseEvent) => {

		const check = (row.charter) && (isAdmin || isCompanyUser || isMainUserLimited);
		const url = check ? `/view/salescharters/${row.charter?.id}/details` : `view/cargos/${row.id}/details`;
		const controlPressed = event.ctrlKey;

		if (controlPressed) {
			window.open(url);
		} else if (row.charter?.id) {
			navigate(url);
		} else {
			window.location.href = url;
		}
	}, []);

	useEffect(() => {
		if (tableKey > 0) {
			clearSelection();
			refetch(0);
		}
	}, [ tableKey ]);

	useEffect(() => {
		getCargosForComments(tableProps.content as CargoFromQuery[]);
	}, [ tableProps.content ]);

	return (
		<React.Fragment>
			<FilterValuesComponent filterValuesProps={filterValuesProps} session={session} />

			<Card disablePadding>
				<Table
					{...tableProps}
					onRowDoubleClick={(row, event) => onRowDoubleClick(row as CargoFromQuery, event)}
					columnResizingEnabled={true}
					filterComponent={() => (
						<CargoFilter
							{...filterProps}
							type={CargoFilterType.UNPLANNED}
							newBreakPoints={undefined}
						/>
					)}
					columns={[ {
						id: "charter",
						title: intl.formatMessage({ id: "CHARTER_ORDER_NUMBER" }),
						render: (row: CargoFromQuery) => <DossierLink charter={row.charter} />,
						sortable: true,
						sortKey: "charter.charterNumber",
						defaultVisible: false,
						width: 150,
					}, {
						id: "lastStatusChange",
						title: intl.formatMessage({ id: "STATUS" }),
						render: (row: CargoFromQuery) => {
							return (
								<StatusChanger
									session={session}
									entity={row}
									statusType={StatusType.Cargo}
									refetch={() => reload()} />
							);
						},
						sortable: true,
						sortKey: "lastStatusChange.status.description",
						disabled: !hasPluginPermission(session, "cargoStatuses"),
						defaultVisible: false,
						width: 175,
					}, {
						id: "relation",
						title: intl.formatMessage({ id: "ORDER_CUSTOMER" }),
						render: (row: CargoFromQuery) => <RelationLink relation={row.charter?.debtor} />,
						sortable: true,
						sortKey: "charter.debtor.name",
						disabled: !(hasPluginPermission(session, "cargoStatuses") && hasOptionalFieldPermission(session, Fields.ORDER, "relation")),
					}, {
						id: "product",
						title: intl.formatMessage({ id: "PRODUCT" }),
						render: (row: CargoFromQuery) => <ProductLink product={row.product} />,
						sortable: true,
						sortKey: "product.name",
						disabled: !hasPluginPermission(session, "products"),
					}, {
						id: "weight",
						title: intl.formatMessage({ id: "WEIGHT" }),
						render: (row: CargoFromQuery) => DataFormat.tonnage(row.weight),
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "weight"),
					}, {
						id: "volume",
						title: intl.formatMessage({ id: "CARGO_VOLUME" }),
						render: (row: CargoFromQuery) => DataFormat.volumeM3(row.volume),
						sortable: false,
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "volume"),
					}, {
						id: "loadedVolume",
						title: intl.formatMessage({ id: "CARGO_VOLUME" }),
						render: (row: CargoFromQuery) => DataFormat.volumeTon(row.loadedVolume),
						sortable: true,
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "volume"),
						defaultVisible: false,
					}, {
						id: "teu",
						title: intl.formatMessage({ id: "TEU" }),
						render: (row: CargoFromQuery) => DataFormat.simpleUnit(row.teu, DataFormat.UNITS.TEU),
						sortable: true,
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "teu"),
						defaultVisible: false,
					}, {
						id: "firstLoadingAction.location",
						title: intl.formatMessage({ id: "ORDER_LOADING_PLACE" }),
						render: (row: CargoFromQuery) => <LocationLink location={row.firstLoadingAction?.location} />,
						sortable: true,
						sortKey: "firstLoadingAction.location.description",
					}, {
						id: "firstLoadingAction.start",
						title: intl.formatMessage({ id: "ORDER_LOADING_DATE" }),
						render: (row: CargoFromQuery) => <CargoActionDate action={row.firstLoadingAction} withoutYear />,
						sortable: true,
						sortKey: "firstLoadingAction.start",
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "loadingDate"),
					}, {
						id: "lastDischargingAction.location",
						title: intl.formatMessage({ id: "ORDER_DISCHARGING_PLACE" }),
						render: (row: CargoFromQuery) => <LocationLink location={row.lastDischargingAction?.location} />,
						sortable: true,
						sortKey: "lastDischargingAction.location.description",
					}, {
						id: "lastDischargingAction.start",
						title: intl.formatMessage({ id: "ORDER_DISCHARGING_DATE" }),
						render: (row: CargoFromQuery) => <CargoActionDate action={row.lastDischargingAction} withoutYear />,
						sortable: true,
						sortKey: "lastDischargingAction.start",
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "dischargingDate"),
					}, {
						id: "actionsWindow",
						title: intl.formatMessage({ id: "START_END_WINDOW" }),
						render: (row: CargoFromQuery) => <FormattedDateWindow from={row.firstLoadingAction?.start} to={row.lastDischargingAction?.end} withoutYear />,
						width: 250,
						defaultVisible: false,
					}, {
						id: "searchLabel",
						title: intl.formatMessage({ id: "ORDER_SEARCH_LABEL" }),
						sortable: true,
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "searchLabel"),
						defaultVisible: false,
					}, {
						id: "firstLoadingAction.viaLocation",
						title: intl.formatMessage({ id: "CARGOS_VIA_LOCATION_LOADING" }),
						render: (row: CargoFromQuery) => <LocationLink location={row.firstLoadingAction?.viaLocation} />,
						sortable: true,
						sortKey: "firstLoadingAction.viaLocation.description",
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "viaLocation"),
						defaultVisible: false,
					}, {
						id: "lastDischargingAction.viaLocation",
						title: intl.formatMessage({ id: "CARGOS_VIA_LOCATION_DISCHARGING" }),
						render: (row: CargoFromQuery) => <LocationLink location={row.lastDischargingAction?.viaLocation} />,
						sortable: true,
						sortKey: "lastDischargingAction.viaLocation.description",
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "viaLocation"),
						defaultVisible: false,
					}, {
						id: "billOfLadingNumber",
						title: intl.formatMessage({ id: "BL_NUMBER" }),
						sortable: true,
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "billOfLading"),
						defaultVisible: false,
					}, {
						id: "bookingReference",
						title: intl.formatMessage({ id: "CARGO_BOOKING_REFERENCE" }),
						sortable: true,
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "bookingReference"),
						defaultVisible: false,
					}, {
						id: "product.adnAttributes.unNumber",
						title: intl.formatMessage({ id: "CARGO_DANGEROUS_GOOD" }),
						render: (row) => row.product?.unNumber ? Language("YES") : Language("NO"),
						defaultVisible: false,
					}, {
						id: "productDetails",
						title: intl.formatMessage({ id: "PRODUCT_DETAILS" }),
						sortable: true,
						defaultVisible: false,
					}, {
						id: "created",
						title: intl.formatMessage({ id: "ORDER_CREATED" }),
						render: (row: CargoFromQuery) => <FormattedDate date={row.created} withoutYear />,
						sortable: true,
						defaultVisible: false,
					}, {
						id: "firstLoadingAction.start",
						title: intl.formatMessage({ id: "ORDER_LOADING_WINDOW" }),
						render: (row: CargoFromQuery) => <FormattedDateWindow from={row.firstLoadingAction?.start} to={row.firstLoadingAction?.end} withoutYear />,
						width: 250,
						sortable: true,
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "loadingWindow"),
						defaultVisible: false,
					}, {
						id: "loadingReference",
						title: intl.formatMessage({ id: "LOADING_REFERENCE" }),
						sortable: true,
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "loadingReference"),
						defaultVisible: false,
					}, {
						id: "lastDischargingAction.start",
						title: intl.formatMessage({ id: "ORDER_DISCHARGING_WINDOW" }),
						render: (row: CargoFromQuery) => <FormattedDateWindow from={row.lastDischargingAction?.start} to={row.lastDischargingAction?.end} withoutYear />,
						width: 250,
						sortable: true,
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "dischargingWindow"),
						defaultVisible: false,
					}, {
						id: "dischargingReference",
						title: intl.formatMessage({ id: "DISCHARGING_REFERENCE" }),
						sortable: true,
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "dischargingReference"),
						defaultVisible: false,
					}, {
						id: "color",
						title: intl.formatMessage({ id: "COLOR" }),
						render: (row: CargoFromQuery) => DataFormat.color(row.color),
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "cargoColor"),
						defaultVisible: false,
					}, {
						id: "deliveryCondition",
						title: intl.formatMessage({ id: "DELIVERY_CONDITION" }),
						sortable: true,
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "deliveryCondition"),
						defaultVisible: true,
						render: (row: CargoFromQuery) => row.deliveryCondition?.description || "",
					} ]}
					actions={isReadOnly ? undefined : [ {
						render: (selection) => (
							<CargoRelationChange
								selection={selection as CargoFromQuery[]}
								refetch={refetch} />
						),
					}, {
						render: () => (
							<RefreshButton
								id="bulkPlanningUnplannedCargos"
								onRefresh={() => refetch()} />
						),
					} ]}
					rowActions={isReadOnly ? undefined : [ {
						icon: <React.Fragment />,
						onClick: () => {},
						render: (row: CargoFromQuery) => (
							<CommentIndicatorButton
								entity={row}
								onClick={() => openComment(row.id, CommentEntityType.Cargo)}
								session={session} />
						),
					} ]}
					dragAndDropEnabled={!isReadOnly}
					dragDropSettings={{
						onDragStart: (e, row: CargoFromQuery) => {
							const currentSelection = (tableProps.tableRef.current?.getSelection?.().selection || []) as CargoFromQuery[];
							setSelectedRows(currentSelection.length > 0 ? currentSelection : [ row ]);
							setDragging(true);
						},
						onDragEnd: (e) => {
							setDragging(false);
							const element = document.elementFromPoint(e.clientX, e.clientY);
							if (element?.classList.contains("droppable")) {
								element.dispatchEvent(new MouseEvent("click", {
									clientX: e.clientX,
									clientY: e.clientY,
									bubbles: true,
									view: window,
									cancelable: true,
								}));
							}
						},
						renderPreview: (_row, selectedRows, _isAllSelected) => {

							const currentSelection = selectedRows.length > 0 ? selectedRows : [ _row ];

							return (
								<div className="cargo-drag">
									<span>{intl.formatMessage({ id: "X_CARGOS_SELECTED" }).replace("_X_", currentSelection.length.toString())}</span>
								</div>
							);
						},
					}} />
			</Card>
		</React.Fragment>
	);
};

const PlannedTable = ({ tableKey, scrollTo, getCargosForComments, openComment }: {
	tableKey: number;
	scrollTo: (callId: number) => void;
	getCargosForComments: (cargos: CargoFromQuery[]) => void;
	openComment: (id: number, entityType: CommentEntityType) => void;
}) => {

	const client = useApolloClient();
	const session = useStoreState(state => state.session);
	const navigate = useNavigate();
	const { isAdmin, isCompanyUser, isMainUserLimited, isReadOnly } = usePermission();

	const { tableProps, filterProps, filterValuesProps, refetch, filters, clearSelection } = useBackendTable({
		id: TableId.BulkPlanning,
		defaultSize: "COMPACT",
		defaultPageSize: 25,
		selectSettings: {
			isFullSelectEnabled: true,
		},
		onChange: async(pagination, sort, filter, callback) => {
			try {
				const cargoSearch = convertFilterToJSON(filter) as Record<string, unknown>;
				delete cargoSearch.charterSubtype;
				delete cargoSearch.charterStatus;
				delete cargoSearch.brokers;

				const result = await client.query({
					query: CARGOS_QUERY,
					variables: {
						pagination: {
							...pagination,
							sortList: sort ?? [ {
								field: "id",
								direction: SortDirection.Asc,
							} ],
						},
						search: {
							...cargoSearch,
							charterSearch: {
								charterSubtypes: filter.charterSubtype,
								statuses: filter.charterStatus ?? [],
								brokers: filter.brokers?.map((option: RelationSelectOption) => option.id),
							},
							isUnplanned: false,
						},
					},
				});

				callback({
					content: result.data.cargos?.content ?? [],
					totalResults: result.data.cargos?.totalElements || 0,
				});
			} catch {
				callback({
					content: [],
					totalResults: 0,
				});
			}
		},
	});

	const reload = () => {
		setTimeout(() => refetch(), 250);
	};

	const onRowDoubleClick = useCallback((row: CargoFromQuery, event: MouseEvent) => {

		const check = (row.charter) && (isAdmin || isCompanyUser || isMainUserLimited);
		const url = check ? `/view/salescharters/${row.charter?.id}/details` : `view/cargos/${row.id}/details`;
		const controlPressed = event.ctrlKey;

		if (controlPressed) {
			window.open(url);
		} else if (row.charter?.id) {
			navigate(url);
		} else {
			window.location.href = url;
		}
	}, []);

	useEffect(() => {
		if (tableKey > 0) {
			refetch(0);
		}
	}, [ tableKey ]);

	useEffect(() => {
		getCargosForComments(tableProps.content as CargoFromQuery[]);
	}, [ tableProps.content ]);

	return (
		<React.Fragment>
			<FilterValuesComponent filterValuesProps={filterValuesProps} session={session} />

			<Card disablePadding>
				<Table
					{...tableProps}
					onRowDoubleClick={(row, event) => onRowDoubleClick(row as CargoFromQuery, event)}
					onRowClick={(row) => {
						const cargo = row as CargoFromQuery;
						if (cargo.firstLoadingAction?.call?.id) {
							scrollTo(cargo.firstLoadingAction.call.id);
						}
					}}
					filterComponent={() => (
						<React.Fragment>
							{/*@ts-expect-error*/}
							<CargoFilter
								{...filterProps}
								type={CargoFilterType.GENERAL} />
						</React.Fragment>

					)}
					columns={[ {
						id: "charterNumber",
						title: intl.formatMessage({ id: "CHARTER_ORDER_NUMBER" }),
						render: (row) => <DossierLink charter={row.charter} />,
						sortable: true,
						sortKey: "charter.charterNumber",
						defaultVisible: false,
						width: 150,
					}, {
						id: "cargoNumber",
						title: intl.formatMessage({ id: "CARGO_NUMBER" }),
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "cargoNumber"),
					}, {
						id: "lastStatusChange",
						title: intl.formatMessage({ id: "STATUS" }),
						render: (row: CargoFromQuery) => {
							return (
								<StatusChanger
									session={session}
									entity={row}
									statusType={StatusType.Cargo}
									refetch={() => reload()} />
							);
						},
						sortable: true,
						sortKey: "lastStatusChange.status.description",
						disabled: !hasPluginPermission(session, "cargoStatuses"),
						defaultVisible: false,
						width: 175,
					}, {
						id: "relation",
						title: intl.formatMessage({ id: "ORDER_CUSTOMER" }),
						render: (row: CargoFromQuery) => <RelationLink relation={row.charter?.debtor} />,
						sortable: true,
						sortKey: "charter.debtor.name",
						disabled: !(hasOptionalFieldPermission(session, Fields.ORDER, "relation")),
					}, {
						id: "ship",
						title: intl.formatMessage({ id: "SHIP" }),
						render: (row: CargoFromQuery) => <ShipLink ship={row.firstLoadingAction?.call?.voyage?.ship} />,
						sortable: true,
						sortKey: "firstLoadingAction.call.voyage.ship.name",
						disabled: !hasPluginPermission(session, "voyages"),
					}, {
						id: "voyages",
						title: intl.formatMessage({ id: "VOYAGE_NUMBER" }),
						render: (row: CargoFromQuery) => <VoyageLink voyage={row.firstLoadingAction?.call?.voyage} />,
						sortable: true,
						sortKey: "firstLoadingAction.call.voyage.voyageNumber",
						width: 200,
						disabled: !hasPluginPermission(session, "voyages"),
					}, {
						id: "product",
						title: intl.formatMessage({ id: "PRODUCT" }),
						render: (row: CargoFromQuery) => <ProductLink product={row.product} />,
						sortable: true,
						sortKey: "product.name",
						disabled: !hasPluginPermission(session, "products"),
					}, {
						id: "weight",
						title: intl.formatMessage({ id: "WEIGHT" }),
						render: (row: CargoFromQuery) => DataFormat.tonnage(row.weight),
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "weight"),
					}, {
						id: "loadedVolume",
						title: intl.formatMessage({ id: "CARGO_VOLUME" }),
						render: (row: CargoFromQuery) => DataFormat.volumeTon(row.loadedVolume),
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "volume"),
						defaultVisible: false,
					}, {
						id: "teu",
						title: intl.formatMessage({ id: "TEU" }),
						render: (row: CargoFromQuery) => DataFormat.simpleUnit(row.teu, DataFormat.UNITS.TEU),
						sortable: true,
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "teu"),
						defaultVisible: false,
					}, {
						id: "firstLoadingAction.location",
						title: intl.formatMessage({ id: "ORDER_LOADING_PLACE" }),
						render: (row: CargoFromQuery) => <LocationLink location={row.firstLoadingAction?.location} />,
						sortable: true,
						sortKey: "firstLoadingAction.location.description",
					}, {
						id: "firstLoadingAction.start",
						title: intl.formatMessage({ id: "ORDER_LOADING_DATE" }),
						render: (row: CargoFromQuery) => <CargoActionDate action={row.firstLoadingAction} withoutYear />,
						sortable: true,
						sortKey: "firstLoadingAction.start",
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "loadingDate"),
					}, {
						id: "lastDischargingAction.location",
						title: intl.formatMessage({ id: "ORDER_DISCHARGING_PLACE" }),
						render: (row: CargoFromQuery) => <LocationLink location={row.lastDischargingAction?.location} />,
						sortable: true,
						sortKey: "lastDischargingAction.location.description",
					}, {
						id: "lastDischargingAction.start",
						title: intl.formatMessage({ id: "ORDER_DISCHARGING_DATE" }),
						render: (row: CargoFromQuery) => <CargoActionDate action={row.lastDischargingAction} withoutYear />,
						sortable: true,
						sortKey: "lastDischargingAction.start",
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "dischargingDate"),
					}, {
						id: "actionsWindow",
						title: intl.formatMessage({ id: "START_END_WINDOW" }),
						render: (row: CargoFromQuery) => <FormattedDateWindow from={row.firstLoadingAction?.start} to={row.lastDischargingAction?.end} withoutYear />,
						width: 250,
						defaultVisible: false,
					}, {
						id: "searchLabel",
						title: intl.formatMessage({ id: "ORDER_SEARCH_LABEL" }),
						sortable: true,
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "searchLabel"),
						defaultVisible: false,
					}, {
						id: "firstLoadingAction.viaLocation",
						title: intl.formatMessage({ id: "CARGOS_VIA_LOCATION_LOADING" }),
						render: (row: CargoFromQuery) => <LocationLink location={row.firstLoadingAction?.viaLocation} />,
						sortable: true,
						sortKey: "firstLoadingAction.viaLocation.description",
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "viaLocation"),
						defaultVisible: false,
					}, {
						id: "lastDischargingAction.viaLocation",
						title: intl.formatMessage({ id: "CARGOS_VIA_LOCATION_DISCHARGING" }),
						render: (row: CargoFromQuery) => <LocationLink location={row.lastDischargingAction?.viaLocation} />,
						sortable: true,
						sortKey: "lastDischargingAction.viaLocation.description",
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "viaLocation"),
						defaultVisible: false,
					}, {
						id: "billOfLadingNumber",
						title: intl.formatMessage({ id: "BL_NUMBER" }),
						sortable: true,
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "billOfLading"),
						defaultVisible: false,
					}, {
						id: "bookingReference",
						title: intl.formatMessage({ id: "CARGO_BOOKING_REFERENCE" }),
						sortable: true,
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "bookingReference"),
						defaultVisible: false,
					}, {
						id: "product.adnAttributes.unNumber",
						title: intl.formatMessage({ id: "CARGO_DANGEROUS_GOOD" }),
						render: (row: CargoFromQuery) => row.product?.adnAttributes?.unNumber ? Language("YES") : Language("NO"),
						defaultVisible: false,
					}, {
						id: "productDetails",
						title: intl.formatMessage({ id: "PRODUCT_DETAILS" }),
						sortable: true,
						defaultVisible: false,
					}, {
						id: "created",
						title: intl.formatMessage({ id: "ORDER_CREATED" }),
						render: (row: CargoFromQuery) => <FormattedDate date={row.created} withoutYear />,
						sortable: true,
						defaultVisible: false,
					}, {
						id: "firstLoadingAction.start",
						title: intl.formatMessage({ id: "ORDER_LOADING_WINDOW" }),
						render: (row: CargoFromQuery) => <FormattedDateWindow from={row.firstLoadingAction?.start} to={row.firstLoadingAction?.end} withoutYear />,
						width: 250,
						sortable: true,
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "loadingWindow"),
						defaultVisible: false,
					}, {
						id: "loadingReference",
						title: intl.formatMessage({ id: "LOADING_REFERENCE" }),
						sortable: true,
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "loadingReference"),
						defaultVisible: false,
					}, {
						id: "lastDischargingAction.start",
						title: intl.formatMessage({ id: "ORDER_DISCHARGING_WINDOW" }),
						render: (row: CargoFromQuery) => <FormattedDateWindow from={row.lastDischargingAction?.start} to={row.lastDischargingAction?.end} withoutYear />,
						width: 250,
						sortable: true,
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "dischargingWindow"),
						defaultVisible: false,
					}, {
						id: "dischargingReference",
						title: intl.formatMessage({ id: "DISCHARGING_REFERENCE" }),
						sortable: true,
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "dischargingReference"),
						defaultVisible: false,
					}, {
						id: "deliveryCondition",
						title: intl.formatMessage({ id: "DELIVERY_CONDITION" }),
						sortable: true,
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "deliveryCondition"),
						defaultVisible: true,
						render: (row: CargoFromQuery) => row.deliveryCondition?.description || "",
					}, {
						id: "color",
						title: intl.formatMessage({ id: "COLOR" }),
						render: (row: CargoFromQuery) => DataFormat.color(row.color),
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "cargoColor"),
						defaultVisible: false,
					} ]}
					actions={isReadOnly ? undefined : [ {
						render: (selection, isFullSelect, event, state) =>
							<CargosDownload
								selection={selection || []}
								isFullSelect={isFullSelect}
								filters={filters}
								columns={state.columnSettings.columns}
								totalResults={tableProps.paginationSettings.totalResults}
								onDownload={() => clearSelection()} />,
					}, {
						render: () => {
							return <RefreshButton id="bulkPlanningPlannedCargos" onRefresh={() => refetch()} />;
						},
					} ]}
					rowActions={isReadOnly ? undefined : [ {
						icon: <React.Fragment />,
						onClick: () => {},
						render: (row) => (
							<CommentIndicatorButton
								entity={row}
								onClick={() => openComment(row.id, CommentEntityType.Cargo)}
								session={session} />
						),
					} ]} />
			</Card>
		</React.Fragment>
	);
};

const FilterValuesComponent = ({ filterValuesProps, session }: { filterValuesProps: Omit<FilterValuesProps, "fields">; session: Session }) => {

	if (filterValuesProps.values.length > 0) {
		return (
			<div style={{ marginBottom: 16 }}>
				<Card>
					<FilterValues
						{...filterValuesProps}
						fields={[ {
							id: "charterNumber",
							title: intl.formatMessage({ id: "CHARTER_ORDER_NUMBER" }),
						}, {
							id: "charterSubtype",
							title: intl.formatMessage({ id: "CHARTER_TYPE" }),
							renderFilterValue: (value) => value.map((type: CharterSubtype) => intl.formatMessage({ id: type })).join(", "),
						}, {
							id: "charterStatus",
							title: intl.formatMessage({ id: "CHARTER_ORDER_STATUS" }),
							renderFilterValue: (value: SalesCharterStatusSelectOption[]) => value?.map((item) => intl.formatMessage({ id: item.languageKeyPrefix + item.name })).join(", "),
						}, {
							id: "currentStatuses",
							title: intl.formatMessage({ id: "STATUS" }),
							renderFilterValue: (value: StatusSelectOption[]) => value?.map((option) => DataFormat.status(option, session.locale)).join(", "),
						}, {
							id: "relations",
							title: intl.formatMessage({ id: "ORDER_CUSTOMER" }),
							renderFilterValue: (value) => value?.map((item: { name: string; }) => item.name).join(", "),
						}, {
							id: "products",
							title: intl.formatMessage({ id: "PRODUCT" }),
							renderFilterValue: (value) => value?.map((item: { name: string; }) => item.name).join(", "),
						}, {
							id: "productDetails",
							title: intl.formatMessage({ id: "PRODUCT_DETAILS" }),
						}, {
							id: "ships",
							title: intl.formatMessage({ id: "VOYAGE_SHIPS" }),
							renderFilterValue: (value) => value?.map((item: { name: string; }) => item.name).join(", "),
						}, {
							id: "voyages",
							title: intl.formatMessage({ id: "VOYAGE_NUMBER" }),
							renderFilterValue: (value) => value.map((item: { voyageNumber: string; }) => item.voyageNumber).join(", "),
						}, {
							id: "currentStatuses",
							title: intl.formatMessage({ id: "STATUS" }),
							renderFilterValue: (value) => value?.map((item: { name: string; }) => item.name).join(", "),
						}, {
							id: "hasDangerousGoods",
							title: intl.formatMessage({ id: "CARGO_DANGEROUS_GOOD" }),
							renderFilterValue: (value) => DataFormat.booleanAsText(value),
						}, {
							id: "loadingCountries",
							title: intl.formatMessage({ id: "ORDER_LOADING_COUNTRY" }),
							renderFilterValue: (value) => countries.find(c => c.code === value)?.name || value,
						}, {
							id: "dischargingCountries",
							title: intl.formatMessage({ id: "ORDER_DISCHARGING_COUNTRY" }),
							renderFilterValue: (value) => countries.find(c => c.code === value)?.name || value,
						}, {
							id: "loadingLocations",
							title: intl.formatMessage({ id: "ORDER_LOADING_PLACE" }),
							renderFilterValue: (value) => value?.map((item: { description: string; }) => item.description).join(", "),
						}, {
							id: "dischargingLocations",
							title: intl.formatMessage({ id: "ORDER_DISCHARGING_PLACE" }),
							renderFilterValue: (value) => value?.map((item: { description: string; }) => item.description).join(", "),
						}, {
							id: "loadingWindow",
							title: intl.formatMessage({ id: "ORDER_LOADING_DATE" }),
							renderFilterValue: (value) => <FormattedDateWindow from={value[0]} to={value[1]} withoutYear />,
						}, {
							id: "dischargingWindow",
							title: intl.formatMessage({ id: "ORDER_DISCHARGING_DATE" }),
							renderFilterValue: (value) => <FormattedDateWindow from={value[0]} to={value[1]} withoutYear />,
						}, {
							id: "searchLabel",
							title: intl.formatMessage({ id: "ORDER_SEARCH_LABEL" }),
						}, {
							id: "loadingReference",
							title: intl.formatMessage({ id: "LOADING_REFERENCE" }),
						}, {
							id: "dischargingReference",
							title: intl.formatMessage({ id: "DISCHARGING_REFERENCE" }),
						}, {
							id: "created",
							title: intl.formatMessage({ id: "ORDER_CREATED" }),
							renderFilterValue: (value) => <FormattedDateWindow from={value[0]} to={value[1]}  withoutYear />,
						}, {
							id: "isPlanned",
							title: intl.formatMessage({ id: "CARGO_PLANNED" }),
							renderFilterValue: (value) => DataFormat.booleanAsText(value),
						} ]} />
				</Card>
			</div>
		);
	} else {
		return null;
	}
};

const CARGOS_QUERY = gql(`#graphql
	query cargos_bulkplanningtable($search: CargoSearch!, $pagination: Pagination) {
		cargos(search: $search, pagination: $pagination) {
			totalElements
			content {
				...CargoPlanningDialogCargo

				id
				cargoNumber
				charter {
					id
					charterNumber
					type
					creditor {
						id
						name
					}
					debtor {
						id
						name
					}
					contract {
						id
					}
				}
				product {
					id
					name
					description
					adnAttributes {
						unNumber
					}
				}
				lastStatusChange {
					...statusChangeFragment
				}
				firstLoadingAction {
					...CargoActionWithVoyage
				}
				lastDischargingAction {
					...CargoActionWithVoyage
				}
				actions {
					start
					end
					weight
					volume
					type
					location {
						id
						description
					}
					surveyor {
						id
						name
					}
				}
				weight
				volume
				loadedVolume
				teu
				searchLabel
				billOfLadingNumber
				bookingReference
				loadingReference
				dischargingReference
				productDetails
				color
				created
				comments {
					...comment
				}
				deliveryCondition {
					description
				}
			}
		}
	}
`);

export default BulkPlanningTable;
